import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, ChevronDown, ChevronUp, RefreshCw, DollarSign, Package, Check, X, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../../../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../../../components/brand/BrandTypography';
import { API_URL } from '../constants';

const SubscriptionTiersTab = () => {
    const [tiers, setTiers] = useState(null);
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedTier, setExpandedTier] = useState(null);
    const [newFeatureName, setNewFeatureName] = useState('');

    useEffect(() => {
        fetchTiersConfig();
    }, []);

    const fetchTiersConfig = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/admin/subscription-tiers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setTiers(data.tiers);
                setFeatures(data.features);
            } else {
                // Use defaults if not found
                setTiers(getDefaultTiers());
                setFeatures(getDefaultFeatures());
            }
        } catch (error) {
            console.error('Error fetching tiers:', error);
            setTiers(getDefaultTiers());
            setFeatures(getDefaultFeatures());
        } finally {
            setLoading(false);
        }
    };

    const getDefaultTiers = () => ([
        {
            id: 'free',
            name: 'Try for Free',
            subtitle: 'Free',
            description: 'Take your time exploring. See if CoRegulate feels right for you.',
            monthlyPrice: 0,
            yearlyPrice: 0,
            color: '#7D8E95',
            highlighted: false,
            cta: 'Start exploring',
            enabledFeatures: ['initial_assessment', 'basic_breathing', 'blog_education'],
            order: 0
        },
        {
            id: 'essentials',
            name: 'Essentials',
            subtitle: 'Get started',
            description: 'Unlock the full grounding toolkit. Perfect for building your practice.',
            monthlyPrice: 9.99,
            yearlyPrice: 79,
            color: '#1D6B6B',
            highlighted: false,
            cta: 'Choose Essentials',
            enabledFeatures: ['initial_assessment', 'basic_breathing', 'blog_education', 'all_breathing', 'micro_reset', 'save_favourites', 'body_scan', 'progress_tracking'],
            order: 1
        },
        {
            id: 'support_plus',
            name: 'Support+',
            subtitle: 'Most chosen',
            description: 'The complete individual toolkit with AI guidance and meditations.',
            monthlyPrice: 19.99,
            yearlyPrice: 149,
            color: '#1D6B6B',
            highlighted: true,
            cta: 'Choose Support+',
            enabledFeatures: ['initial_assessment', 'basic_breathing', 'blog_education', 'all_breathing', 'micro_reset', 'save_favourites', 'body_scan', 'progress_tracking', 'ai_guide', 'meditations', 'self_talk', 'full_library', 'repair_scripts'],
            order: 2
        },
        {
            id: 'partner',
            name: 'Partner',
            subtitle: 'For couples',
            description: 'The complete toolkit for you and someone you trust. Regulate together.',
            monthlyPrice: 34.99,
            yearlyPrice: 249,
            color: '#E08850',
            highlighted: false,
            cta: 'Choose Partner',
            enabledFeatures: ['initial_assessment', 'basic_breathing', 'blog_education', 'all_breathing', 'micro_reset', 'save_favourites', 'body_scan', 'progress_tracking', 'ai_guide', 'meditations', 'self_talk', 'full_library', 'repair_scripts', 'partner_mode', 'shared_sessions', 'communication_scripts', 'relationship_templates', 'priority_features'],
            order: 3
        }
    ]);

    const getDefaultFeatures = () => ([
        { id: 'initial_assessment', name: 'Initial assessment', category: 'Core', description: 'Personalized assessment to understand your needs' },
        { id: 'basic_breathing', name: '3 breathing patterns', category: 'Core', description: 'Box breathing, 4-7-8, and Physiological Sigh' },
        { id: 'blog_education', name: 'Blog & educational content', category: 'Core', description: 'Access to all educational articles' },
        { id: 'all_breathing', name: 'All breathing patterns (10+)', category: 'Essentials', description: 'Full library of breathing exercises' },
        { id: 'micro_reset', name: 'Micro-reset tool', category: 'Essentials', description: 'Quick 30-second grounding moments' },
        { id: 'save_favourites', name: 'Save favourite tools', category: 'Essentials', description: 'Bookmark your go-to exercises' },
        { id: 'body_scan', name: 'Body scan meditation', category: 'Essentials', description: 'Guided body awareness practice' },
        { id: 'progress_tracking', name: 'Progress tracking', category: 'Essentials', description: 'Track your regulation journey' },
        { id: 'ai_guide', name: 'AI Guide companion', category: 'Support+', description: 'Personalized AI-powered guidance' },
        { id: 'meditations', name: 'Guided meditations', category: 'Support+', description: 'Full meditation library with audio' },
        { id: 'self_talk', name: 'Self-talk reframes', category: 'Support+', description: 'Cognitive reframing exercises' },
        { id: 'full_library', name: 'Full grounding library', category: 'Support+', description: 'Access to all regulation tools' },
        { id: 'repair_scripts', name: 'Pause + Repair scripts', category: 'Support+', description: 'Relationship repair mini-scripts' },
        { id: 'partner_mode', name: 'Partner co-regulation mode', category: 'Partner', description: 'Regulate together with your partner' },
        { id: 'shared_sessions', name: 'Shared grounding sessions', category: 'Partner', description: 'Real-time shared exercises' },
        { id: 'communication_scripts', name: 'Full communication scripts', category: 'Partner', description: 'Complete relationship communication guides' },
        { id: 'relationship_templates', name: 'Relationship templates', category: 'Partner', description: 'Check-in and conversation templates' },
        { id: 'priority_features', name: 'Priority new features', category: 'Partner', description: 'Early access to new tools' }
    ]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/admin/subscription-tiers`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ tiers, features })
            });
            
            if (response.ok) {
                toast.success('Subscription tiers saved successfully');
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            console.error('Error saving tiers:', error);
            toast.error('Failed to save subscription tiers');
        } finally {
            setSaving(false);
        }
    };

    const updateTier = (tierId, field, value) => {
        setTiers(prev => prev.map(t => 
            t.id === tierId ? { ...t, [field]: value } : t
        ));
    };

    const toggleFeature = (tierId, featureId) => {
        setTiers(prev => prev.map(t => {
            if (t.id !== tierId) return t;
            const enabled = t.enabledFeatures.includes(featureId);
            return {
                ...t,
                enabledFeatures: enabled
                    ? t.enabledFeatures.filter(f => f !== featureId)
                    : [...t.enabledFeatures, featureId]
            };
        }));
    };

    const addFeature = () => {
        if (!newFeatureName.trim()) return;
        const featureId = newFeatureName.toLowerCase().replace(/\s+/g, '_');
        if (features.some(f => f.id === featureId)) {
            toast.error('Feature already exists');
            return;
        }
        setFeatures(prev => [...prev, {
            id: featureId,
            name: newFeatureName,
            category: 'Custom',
            description: ''
        }]);
        setNewFeatureName('');
        toast.success('Feature added');
    };

    const removeFeature = (featureId) => {
        setFeatures(prev => prev.filter(f => f.id !== featureId));
        // Also remove from all tiers
        setTiers(prev => prev.map(t => ({
            ...t,
            enabledFeatures: t.enabledFeatures.filter(f => f !== featureId)
        })));
    };

    const updateFeature = (featureId, field, value) => {
        setFeatures(prev => prev.map(f =>
            f.id === featureId ? { ...f, [field]: value } : f
        ));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-[#1D6B6B]" />
            </div>
        );
    }

    const categories = [...new Set(features.map(f => f.category))];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <Heading level={2} className="text-[#1D6B6B]">Subscription Tiers</Heading>
                    <SmallText className="text-[#7D8E95]">
                        Configure pricing, features, and descriptions for each tier
                    </SmallText>
                </div>
                <PrimaryButton onClick={handleSave} disabled={saving}>
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                </PrimaryButton>
            </div>

            {/* Tiers Configuration */}
            <div className="space-y-4">
                <Heading level={3} className="text-[#1D6B6B]">Pricing Tiers</Heading>
                
                {tiers.sort((a, b) => a.order - b.order).map((tier) => (
                    <div key={tier.id} className="glass-card rounded-xl overflow-hidden">
                        <button
                            onClick={() => setExpandedTier(expandedTier === tier.id ? null : tier.id)}
                            className="w-full flex items-center justify-between p-4 bg-white/40 hover:bg-white/60 transition-colors"
                        >
                            <div className="flex items-center gap-4">
                                <div 
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: tier.color }}
                                />
                                <div className="text-left">
                                    <span className="font-medium text-[#1D6B6B]">{tier.name}</span>
                                    <span className="text-[#7D8E95] text-sm ml-2">
                                        ${tier.monthlyPrice}/mo • ${tier.yearlyPrice}/yr
                                    </span>
                                </div>
                                {tier.highlighted && (
                                    <span className="text-xs bg-[#1D6B6B] text-white px-2 py-0.5 rounded-full">
                                        Highlighted
                                    </span>
                                )}
                            </div>
                            {expandedTier === tier.id ? (
                                <ChevronUp className="w-5 h-5 text-[#7D8E95]" />
                            ) : (
                                <ChevronDown className="w-5 h-5 text-[#7D8E95]" />
                            )}
                        </button>
                        
                        {expandedTier === tier.id && (
                            <div className="p-4 space-y-4 border-t border-[#FFBB98]/30">
                                {/* Basic Info */}
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm text-[#7D8E95] mb-1">Tier Name</label>
                                        <input
                                            type="text"
                                            value={tier.name}
                                            onChange={(e) => updateTier(tier.id, 'name', e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-[#7D8E95] mb-1">Subtitle/Badge</label>
                                        <input
                                            type="text"
                                            value={tier.subtitle}
                                            onChange={(e) => updateTier(tier.id, 'subtitle', e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm text-[#7D8E95] mb-1">Description</label>
                                    <textarea
                                        value={tier.description}
                                        onChange={(e) => updateTier(tier.id, 'description', e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                                    />
                                </div>

                                {/* Pricing */}
                                <div className="grid md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm text-[#7D8E95] mb-1">Monthly Price ($)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={tier.monthlyPrice}
                                            onChange={(e) => updateTier(tier.id, 'monthlyPrice', parseFloat(e.target.value) || 0)}
                                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-[#7D8E95] mb-1">Yearly Price ($)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={tier.yearlyPrice}
                                            onChange={(e) => updateTier(tier.id, 'yearlyPrice', parseFloat(e.target.value) || 0)}
                                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-[#7D8E95] mb-1">CTA Button Text</label>
                                        <input
                                            type="text"
                                            value={tier.cta}
                                            onChange={(e) => updateTier(tier.id, 'cta', e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                                        />
                                    </div>
                                </div>

                                {/* Display Options */}
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm text-[#7D8E95] mb-1">Accent Color</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={tier.color}
                                                onChange={(e) => updateTier(tier.id, 'color', e.target.value)}
                                                className="w-10 h-10 rounded border-0 cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                value={tier.color}
                                                onChange={(e) => updateTier(tier.id, 'color', e.target.value)}
                                                className="flex-1 px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 pt-6">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={tier.highlighted}
                                                onChange={(e) => updateTier(tier.id, 'highlighted', e.target.checked)}
                                                className="w-4 h-4 rounded"
                                            />
                                            <span className="text-sm text-[#3A3A37]">Highlight this tier (recommended)</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Features Toggle */}
                                <div className="pt-4 border-t border-[#FFBB98]/30">
                                    <label className="block text-sm text-[#7D8E95] mb-3">Enabled Features</label>
                                    <div className="space-y-3">
                                        {categories.map(category => (
                                            <div key={category}>
                                                <SmallText className="text-[#1D6B6B] font-medium mb-2 block">{category}</SmallText>
                                                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                                                    {features.filter(f => f.category === category).map(feature => (
                                                        <button
                                                            key={feature.id}
                                                            onClick={() => toggleFeature(tier.id, feature.id)}
                                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                                                                tier.enabledFeatures.includes(feature.id)
                                                                    ? 'bg-[#1D6B6B]/10 text-[#1D6B6B] border border-[#1D6B6B]/30'
                                                                    : 'bg-white/40 text-[#7D8E95] border border-transparent hover:border-[#7D8E95]/30'
                                                            }`}
                                                        >
                                                            {tier.enabledFeatures.includes(feature.id) ? (
                                                                <Check className="w-4 h-4 flex-shrink-0" />
                                                            ) : (
                                                                <X className="w-4 h-4 flex-shrink-0 opacity-30" />
                                                            )}
                                                            <span className="truncate">{feature.name}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Features Management */}
            <div className="glass-card-sage rounded-xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <Heading level={3} className="text-[#1D6B6B]">Feature Library</Heading>
                    <SmallText className="text-[#7D8E95]">{features.length} features</SmallText>
                </div>
                
                {/* Add New Feature */}
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={newFeatureName}
                        onChange={(e) => setNewFeatureName(e.target.value)}
                        placeholder="New feature name..."
                        className="flex-1 px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                        onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                    />
                    <SecondaryButton onClick={addFeature}>
                        <Plus className="w-4 h-4" />
                        Add
                    </SecondaryButton>
                </div>

                {/* Features List by Category */}
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                    {categories.map(category => (
                        <div key={category}>
                            <SmallText className="text-[#1D6B6B] font-medium mb-2 block">{category}</SmallText>
                            <div className="space-y-2">
                                {features.filter(f => f.category === category).map(feature => (
                                    <div 
                                        key={feature.id}
                                        className="flex items-center gap-3 bg-white/40 rounded-lg p-3"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <input
                                                type="text"
                                                value={feature.name}
                                                onChange={(e) => updateFeature(feature.id, 'name', e.target.value)}
                                                className="w-full px-2 py-1 rounded border border-transparent hover:border-[#FFBB98]/30 bg-transparent text-sm"
                                            />
                                        </div>
                                        <select
                                            value={feature.category}
                                            onChange={(e) => updateFeature(feature.id, 'category', e.target.value)}
                                            className="px-2 py-1 rounded border border-[#FFBB98]/30 bg-white/60 text-xs"
                                        >
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                            <option value="Custom">Custom</option>
                                        </select>
                                        <button
                                            onClick={() => removeFeature(feature.id)}
                                            className="text-red-400 hover:text-red-500 p-1"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Save Button (bottom) */}
            <div className="flex justify-end">
                <PrimaryButton onClick={handleSave} disabled={saving} size="lg">
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save All Changes'}
                </PrimaryButton>
            </div>
        </div>
    );
};

export default SubscriptionTiersTab;
